import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { WishlistFacade } from '../../../facades/wishlist.facade';
import { from, Observable, of, Subject } from 'rxjs';
import { catchError, concatMap, take, takeUntil, tap } from 'rxjs/operators';
import { IBaseWishlist, IWishlistProductDetails } from '../../../models/wishlist.models';
import { PayloadUtils } from '../../../utils/payload.utils';
import { AnalyticsService } from '../../../analytics/analytics.service';
import { MarketingFacade } from '../../../facades/marketing.facade';
import { ConfigurationFacade } from '../../../facades/configuration.facade';
import { MathUtils } from '../../../utils/math.utils';
import { ICart } from '../../../models/cart.models';
import { IconType } from '../../../models/settings.model';
import {
  CONFIG_PRICING_GROUP_FOR_CONSUMABLES,
  EFeatureToggles,
  EInstalledBaseTabs,
  EStoreFeatures,
  EStoreTypes,
} from '../../../configurations/common';
import { AppUtils } from '../../../utils/app.utils';
import { PriceUtils } from '../../../utils/price.utils';
import { IPrice, ISystemDetails } from '../../../models/common.models';
import { CatalogFacade } from '../../../facades/catalog.facade';
import { IInstallBaseQueryParams } from '../../../models/catalog.models';
import { CustomerFacade } from '../../../facades/customer.facade';
import { CartUtils } from '../../../utils/cart.utils';
import { ImageUtils } from '../../../utils/image.utils';
import { ImagePreset } from '../../../models/image.models';

@Component({
  selector: 'app-wishlist',
  templateUrl: './wishlist.component.html',
  styleUrls: ['./wishlist.component.scss'],
  standalone: false,
})
export class WishlistComponent implements OnInit, OnDestroy {
  private readonly router: Router = inject(Router);
  private readonly route: ActivatedRoute = inject(ActivatedRoute);
  private readonly wishlistFacade: WishlistFacade = inject(WishlistFacade);
  private readonly analyticsService: AnalyticsService = inject(AnalyticsService);
  private readonly marketingFacade: MarketingFacade = inject(MarketingFacade);
  private readonly configurationFacade: ConfigurationFacade = inject(ConfigurationFacade);
  private readonly catalogFacade: CatalogFacade = inject(CatalogFacade);
  private readonly customerFacade: CustomerFacade = inject(CustomerFacade);

  protected readonly PriceUtils: typeof PriceUtils = PriceUtils;
  protected readonly ImageUtils: typeof ImageUtils = ImageUtils;
  protected readonly ImagePreset: typeof ImagePreset = ImagePreset;

  iconType: typeof IconType = IconType;
  selectedProducts: string[] = [];
  selectAllValue: boolean = false;
  editModalActive: boolean = false;
  removeModalActive: boolean = false;
  addItemToCartModalActive: boolean = false;
  showModalSelectEquipment: boolean = false;
  currency!: string;
  wishlistId!: string;
  wishlist!: IBaseWishlist;
  wishlistProducts: IWishlistProductDetails[] = [];
  itemsLoaded: boolean = false;
  itemsInQueueToCart: string[] = [];
  itemsInQueueToRemove: string[] = [];
  editInProgress: boolean = false;
  wishlistInProgress: any[] = [];
  loadingCartDataInProgress: boolean = true;
  isSapIntegration: boolean = false;
  isSapIntegrationRequired: boolean = false;
  delayTimer: any;
  isUsStore: boolean = false;
  isCaStore: boolean = false;
  currentCart?: ICart;
  currentCartId!: string;
  hasCartContract: boolean = false;
  items: any;
  excludeTax: boolean = false;
  pricingGroupForConsumables!: string;
  isBusinessPartner: boolean = false;
  pricingGroupToggle: boolean = false;
  productPricingGroup: string = '';
  isMyInstalledBaseFlowEnabled: boolean = false;
  isPredefinedEquipmentFlowEnabled: boolean = false;
  isNonEquipmentPurchaseEnabled: boolean = false;
  supportedCategoriesForNonEquipmentPurchase: string[] = [];

  private unsubscribe$: Subject<void> = new Subject<void>();

  ngOnInit(): void {
    this.currency = PriceUtils.getStoreCurrencyCode();
    this.isUsStore = AppUtils.isStoreActive(EStoreTypes.US);
    this.isCaStore = AppUtils.isStoreActive(EStoreTypes.CA);
    this.excludeTax = this.configurationFacade.isFeatureAvailable(EStoreFeatures.EXCLUDE_TAX);
    this._selectIsPredefinedEquipmentFlowEnabled();
    this._isNonEquipmentPurchaseEnabled();
    this.configurationFacade.getSapStatus().subscribe(({sapIntegration, sapIntegrationRequired}) => {
      this.isSapIntegration = sapIntegration;
      this.isSapIntegrationRequired = sapIntegrationRequired;
    });

    if (this.isUsStore) {
      this.selectHasCartContract();
    }

    this.route.params.subscribe(params => {
      if (params.id) {
        this.wishlistId = params.id;
        this.getWishlist(this.wishlistId);
      }
    });

    this.selectIsMyInstalledBaseFlowEnabled();
    this.selectCurrentCart();
    this.selectLoadingCartDataInProgress();
    this.selectItemsInQueueToCart();
    this.getLastUpdatedProduct();
    this.selectWishlistInProgress();
    this.selectPricingGroupToggle();
    this.selectIsCustomerBusinessPartner();
    this.selectPricingGroupForConsumables();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.complete();
    this.unsubscribe$.unsubscribe();
  }

  private selectPricingGroupForConsumables(): void {
    this.catalogFacade.getPricingGroupForConsumables().pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(pricingGroup => this.pricingGroupForConsumables = pricingGroup);
  }


  /**
   * Get wishlist based on id and get all items with details.
   *
   * @param {string} id
   */
  getWishlist(id: string): void {
    this.wishlistFacade.getWishlist(id).pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(
      result => {
        this.wishlist = result.data;
        if (result?.included) {
          this.getItems(result.included);
        }
        this.itemsLoaded = true;
      },
    );
  }

  getLastUpdatedProduct(): void {
    this.wishlistFacade.selectLastUpdatedProduct().pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(
      updatedProduct => {
        if (updatedProduct) {
          this.wishlistProducts = this.wishlistProducts.reduce<IWishlistProductDetails[]>((prev: IWishlistProductDetails[], curr: IWishlistProductDetails): IWishlistProductDetails[] => {
            const product: IWishlistProductDetails = {...curr};
            if (curr.sku === updatedProduct.sku) {
              product.qty = updatedProduct.qty;
            }
            prev.push(product);
            return prev;
          }, []);
        }
      },
    );
  }

  /**
   * Get details of all shopping-list-items inside "included":
   * - quantity (always set to 1),
   * - externalUrlSmall for image,
   * - name,
   * - sku,
   * - materialNumber (non SAP - material_number, SAP - sap_material_number),
   * - productAbstractSku
   * - defaultPrice
   * - secondPrice
   * - pricingGroup
   * - webshopProductCategory
   *
   * @param included
   */
  getItems(included: any[]): void {
    const items: any[] = included.filter((item: any): boolean => item.type === 'shopping-list-items');

    this.wishlistProducts = items.reduce<IWishlistProductDetails[]>((acc: IWishlistProductDetails[], next: any): IWishlistProductDetails[] => {
      acc.push({
        ...PayloadUtils.getConcreteProductDetails(included, next.attributes.sku),
        qty: next.attributes.quantity,
        shoppingListItemId: next.id,
      });
      return acc;
    }, []);
  }

  /**
   * Return sum of all quantities (in template it is also used for different translations for product/products).
   *
   * @returns {number}
   */
  getProductsQty(): number {
    return this.wishlistProducts.reduce((prev: number, curr: IWishlistProductDetails) => {
      return prev + curr.qty;
    }, 0);
  }

  /**
   * Select/deselect product and add/remove it from "selectedProducts" array. If "selectedProducts" array is empty
   * then deselect "Select all" CheckBox.
   *
   * @param product
   */
  selectProduct(product: any): void {
    const index: number = this.selectedProducts.indexOf(product.sku);
    if (index > -1) {
      this.selectedProducts.splice(index, 1);
    } else {
      this.selectedProducts.push(product.sku);
    }

    if (this.selectedProducts.length === 0) {
      this.selectAllValue = false;
    }
  }

  selectAll(event: any): void {
    if (event.target.checked) {
        this.selectedProducts = this.wishlistProducts.map((product: IWishlistProductDetails): string => product.sku);
    } else {
      this.selectedProducts = [];
    }
  }

  /**
   * Make 0.5s delay when changing products quantity. During this 0.5s there are disabled buttons for adding to cart
   * or removing from wishlist.
   *
   * @param {string} sku
   * @param qty
   */
  updateProductQty(sku: string, qty: any): void {
    clearTimeout(this.delayTimer);
    this.delayTimer = setTimeout(() => {
      const updatedQty: number = parseInt(qty, 10);
      if (!MathUtils.checkIfNumeric(updatedQty) || qty === 0) {
        return;
      }
      this.wishlistFacade.updateItem(this.wishlistId, this.getShoppingListId(sku), sku, updatedQty);
    }, 500);
  }

  /**
   * Calculate price of all wishlist products (item price * quantity).
   *
   * @returns {number}
   */
  getTotalPrice(): number {
    return this.wishlistProducts.reduce(
      (prev: number, curr: IWishlistProductDetails): number =>
        prev + (curr.defaultPrice ? PriceUtils.getPriceValue(curr.defaultPrice) ?? 0 : 0) * curr.qty,
      0,
    );
  }

  /**
   * If user manually checks all products, then select automatically "Select all".
   *
   * @returns {boolean}
   */
  isAllProductsSelected(): boolean {
    return this.selectedProducts.length === this.wishlistProducts.length;
  }

  /**
   * Method to get all selected products which are available for online purchase
   *
   * @returns {IWishlistProductDetails[]}
   * @private
   */
  private getAvailableSelectedProducts(): IWishlistProductDetails[] {
    return this.wishlistProducts.filter(
      product =>
        this.checkIfProductIsAvailableForOnlinePurchase(product)
        && this.selectedProducts.includes(product.sku),
    );
  }

  /**
   * Method checks if of selected products is at least one which is available for online purchase
   *
   * @returns {boolean}
   * @private
   */
  areSelectedProductAvailable(): boolean {
    return this.getAvailableSelectedProducts().length > 0;
  }

  wishlistEdited(event: any): void {
    if (event !== 'error') {
      this.wishlist = {
        ...this.wishlist,
        attributes: {
          ...event.attributes,
        },
      };
    }
    this.editInProgress = false;
  }

  wishlistRemoved(): void {
    this.wishlistFacade.deleteList(this.wishlistId);
    this.router.navigate(['/wishlists']).then();
  }

  selectEquipment(): void {
    let commands: string = '/my-installed-base';
    let tab: EInstalledBaseTabs | undefined = EInstalledBaseTabs.EQUIPMENT;
    if (this.isBusinessPartner) {
      commands = '/equipment-selection';
      tab = undefined;
    }
    let queryParams: Partial<IInstallBaseQueryParams> = tab ? {tab} : {};
    if (this.pricingGroupToggle) {
      queryParams = {
        ...queryParams,
        pricing_group: this.productPricingGroup,
      };
    }
    this.router.navigate([commands], {
      queryParams: queryParams,
    }).then();
  }

  /**
   * Method to select Pricing Group toggle
   * @private
   */
  private selectPricingGroupToggle(): void {
    this.configurationFacade.isFeatureEnabled(EFeatureToggles.PRODUCT_PRICING_GROUP).pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(value => {
      this.pricingGroupToggle = value;
    });
  }


  selectCurrentCart(): void {
    this.marketingFacade.selectCart().pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(cart => {
      if (cart) {
        this.currentCart = cart;
        this.currentCartId = cart.id;
      }
    });
  }

  selectItemsInQueueToCart(): void {
    this.marketingFacade.selectItemsInQueueToCart()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(state => {
        this.itemsInQueueToCart = state;
      });
  }

  selectLoadingCartDataInProgress(): void {
    this.marketingFacade.selectLoadingCartDataInProgress().pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(loadingCartDataInProgress => {
        this.loadingCartDataInProgress = loadingCartDataInProgress;
      },
    );
  }

  selectWishlistInProgress(): void {
    this.wishlistFacade.selectListInProgress()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(state => {
        this.wishlistInProgress = state;
      });
  }

  selectHasCartContract(): void {
    this.marketingFacade.selectHasCartContract().pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(value => {
        this.hasCartContract = value;
      },
    );
  }

  isItemInProgress(sku: string): boolean {
    return this.itemsInQueueToCart.includes(sku)
      || this.itemsInQueueToRemove.includes(sku)
      || !!this.wishlistInProgress.find((list: any): boolean => list.sku === sku);
  }

  /**
   * Add specific product to cart.
   * If equipment is required and not selected, open modal to select equipment.
   * If cart has contract, open modal to confirm adding item to cart.
   * Otherwise, add item directly to cart.
   *
   * @param product IWishlistProductDetails
   */
  addProductToCart(product: IWishlistProductDetails): void {
    if (this.isItemInProgress(product.sku) || this.loadingCartDataInProgress) {
      return;
    }

    const wishlistProduct: IWishlistProductDetails | undefined = this.wishlistProducts.find((item: IWishlistProductDetails): boolean => item.sku === product.sku);
    if (!wishlistProduct) {
      return;
    }
    const quantity: number = wishlistProduct.qty;
    const systemDetails: ISystemDetails | undefined = this.currentCart?.attributes?.systemDetails;

    const isEquipmentRequired: boolean = CartUtils.determineIfEquipmentIsRequired(
      this.isMyInstalledBaseFlowEnabled,
      this.isNonEquipmentPurchaseEnabled,
      this.supportedCategoriesForNonEquipmentPurchase,
      product?.webshopProductCategory ?? '',
    );


    if (!this.isPredefinedEquipmentFlowEnabled && isEquipmentRequired) {
      if (!systemDetails?.siemensEquipmentId) {
        this.productPricingGroup = product?.pricingGroup ?? '';
        this.showModalSelectEquipment = true;
        return;
      }
    }

    if (this.hasCartContract) {
      this.items = [{
        data: {
          sku: product?.sku,
          name: product?.name || product?.sku,
        },
        isConcrete: true,
        quantity: quantity,
      }];
      this.addItemToCartModalActive = true;
      return;
    }

    this.addToCart(product, quantity);
  }

  private addToCart(item: IWishlistProductDetails, quantity: number): void {
    this.analyticsService.setProducts(item);
    this.analyticsService.trackCart('cart.add');
    this.marketingFacade.addProductToCart({
      sku: item.sku,
      name: item.name,
      sap_product_sparepart: item.sap_product_sparepart,
    }, true, quantity);
  }

  /**
   * Remove specific item via "X" button.
   *
   * @param {string} sku
   */
  removeItemFromWishlist(sku: string): void {
    if (this.isItemInProgress(sku)) {
      return;
    }
    this.itemsInQueueToRemove.push(sku);
    this.removeItem(sku)
      .pipe(take(1))
      .subscribe();
  }

  /**
   * Remove all selected items via "Remove" button.
   */
  removeAllSelectedItems(): void {
    if (this.selectedProducts.length > 0) {
      this.itemsInQueueToRemove.push(...this.selectedProducts);
      from(this.selectedProducts)
        .pipe(
          concatMap((sku: string): Observable<void> => this.removeItem(sku)),
          takeUntil(this.unsubscribe$),
        )
        .subscribe({
          complete: () => {
            this.selectedProducts = [];
          },
        });
    }
  }

  addAllSelectedToCart(): void {
    if (this.selectedProducts.length === 0 || this.loadingCartDataInProgress) {
      return;
    }
    const systemDetails: ISystemDetails | undefined = this.currentCart?.attributes?.systemDetails;

    if (this.isMyInstalledBaseFlowEnabled && !systemDetails?.siemensEquipmentId) {
      this.showModalSelectEquipment = true;
      return;
    }

    if (this.hasCartContract) {
      this.items = [];
      this.selectedProducts.forEach((sku: string): void => {
        const item: IWishlistProductDetails | undefined = this.wishlistProducts.find((product: IWishlistProductDetails): boolean => product.sku === sku);
        if (item && this.checkIfProductIsAvailableForOnlinePurchase(item)) {
          this.items.push({
            data: item,
            isConcrete: true,
            quantity: item.qty,
          });
        }
      });
      this.addItemToCartModalActive = true;
      return;
    }

    this.selectedProducts.forEach((sku: string): void => {
      const item: IWishlistProductDetails | undefined = this.wishlistProducts.find((product: IWishlistProductDetails): boolean => product.sku === sku);
      if (item && this.checkIfProductIsAvailableForOnlinePurchase(item)) {
        this.addToCart(item, item.qty);
      }
    });
  }

  /**
   * Toggle for "disabled" attribute of "Add to cart" button.
   *
   * @returns {boolean}
   */
  isAddToCartDisabled(): boolean {
    return this.loadingCartDataInProgress || (!this.isSapIntegration && this.isSapIntegrationRequired);
  }

  getShoppingListId(sku: string): string {
    return this.wishlistProducts.find((product: IWishlistProductDetails): boolean => product.sku === sku)?.shoppingListItemId ?? '';
  }

  /**
   * Called after removing item/items via "X" or "Remove" button. After successful deletion of item from wishlist remove
   * it also from "wishlistProducts" and "selectedProducts" arrays.
   *
   * @param {string} sku
   * @private
   * @returns {Observable<void>} An Observable that completes when the removal operation finishes
   */
  private removeItem(sku: string): Observable<void> {
    return this.wishlistFacade.removeItemFromWishlist(this.wishlistId, this.getShoppingListId(sku))
      .pipe(
        take(1),
        tap(() => {
          this.wishlistProducts = this.wishlistProducts.filter(product => product.sku !== sku);
          this.selectedProducts = this.selectedProducts.filter(selectedProductSku => selectedProductSku !== sku);
          this.itemsInQueueToRemove = this.itemsInQueueToRemove.filter(queueSku => queueSku !== sku);
        }),
        catchError(err => {
          const message: string = err?.error.errors?.[0]?.detail ? err.error.errors[0].detail : err.message;
          this.configurationFacade.setAlert({type: 'error', message});
          return of();
        }),
      );
  }

  /**
   * Method to select if customer is business partner
   */
  private selectIsCustomerBusinessPartner(): void {
    this.customerFacade.isBusinessPartner().pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(isBusinessPartner => {
      this.isBusinessPartner = isBusinessPartner;
    });
  }

  edit(): void {
    if (!this.editInProgress) {
      this.editModalActive = true;
    }
  }

  redirectToProductLists(): void {
    this.router.navigate(['/catalog']).then();
  }

  /**
   * Returns true if product is available for online purchase
   *  user has to be non-business partner
   *  FL has to be selected
   *  product is available for online purchase
   *    if product is consumable and system details is not isSparePartsAvailable
   *    or system details is isSparePartsAvailable
   *
   * @return {boolean}
   */
  checkIfProductIsAvailableForOnlinePurchase(product: IWishlistProductDetails): boolean {
    if (
      !this.isBusinessPartner
      && this.currentCart?.attributes?.systemDetails?.siemensEquipmentId
      && this._isPricingGroupAvailableForCurrentStore()
    ) {
      return (
        product.pricingGroup === this.pricingGroupForConsumables
        && !this.currentCart?.attributes?.systemDetails.isSparePartsAvailable
      ) || this.currentCart?.attributes?.systemDetails.isSparePartsAvailable;
    } else {
      return true;
    }
  }

  /**
   * In case the pricingGroupForConsumables is equal to its own translate key,
   * it means the pricing group is not set for current store
   *
   * @returns {boolean}
   * @private
   */
  private _isPricingGroupAvailableForCurrentStore(): boolean {
    return this.pricingGroupForConsumables !== CONFIG_PRICING_GROUP_FOR_CONSUMABLES;
  }

  /**
   * Method for retrieving IsMyInstalledBaseFlowEnabled feature toggle value
   * @private
   */
  private selectIsMyInstalledBaseFlowEnabled(): void {
    this.configurationFacade.isFeatureEnabled(EFeatureToggles.MY_INSTALLED_BASE_FLOW).pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(isMyInstalledBaseFlowEnabled => {
      this.isMyInstalledBaseFlowEnabled = isMyInstalledBaseFlowEnabled;
    });
  }

  isExcludeTaxActive(): boolean {
    return this.configurationFacade.isFeatureAvailable(EStoreFeatures.EXCLUDE_TAX);
  }

  isPriceVisible(defaultPrice?: IPrice): boolean {
    return defaultPrice ? (PriceUtils.getPriceValue(defaultPrice) ?? 0) > 0 && !AppUtils.isSapStore() : false;
  }

  /**
   * Method for retrieving IsPredefinedEquipmentFlowEnabled feature toggle value
   * @private
   */
  private _selectIsPredefinedEquipmentFlowEnabled(): void {
    this.configurationFacade.isFeatureEnabled(EFeatureToggles.PREDEFINED_EQUIPMENT_FLOW).pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(isPredefinedEquipmentFlowEnabled => {
      this.isPredefinedEquipmentFlowEnabled = isPredefinedEquipmentFlowEnabled;
    });
  }

  /**
   * Method for retrieving IsNonEquipmentPurchaseEnabled feature toggle value
   * @private
   */
  private _isNonEquipmentPurchaseEnabled(): void {
    this.configurationFacade.isFeatureEnabled(EFeatureToggles.NON_EQUIPMENT_PURCHASE).pipe(
      tap((isEnabled: boolean) => {
        if (isEnabled) {
          this.configurationFacade.getNonEquipmentPurchaseSupportedCategories$().pipe(
            take(1),
          ).subscribe((categories: string[]) => {
            this.supportedCategoriesForNonEquipmentPurchase = categories;
          });
        }
        this.isNonEquipmentPurchaseEnabled = isEnabled;
      }),
      take(1),
    ).subscribe();
  }
}
